import { Box, Grid, Tab, Tabs } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
    IComboBoxOption, IMaterial, IMaterialInputDialogProps, IMaterialItem, IMaterialNote, MaterialContext
} from '../../interfaces';
import { Values } from '../../utils';
import { TabPanel } from "../Shared/TabPanel";
import { ImportFormSection } from './ImportFormSection';
import { LocationFormSection } from './LocationFormSection';
import { MaterialDescriptionFormSection } from './MaterialDescriptionFormSection';
import { NotesFormSection } from './NotesFormSection';
import { PartIdentificationFormSection } from './PartIdentificationFormSection';
import { RestrictionsFormSection } from './RestrictionsFormSection';
import { ValuationFormSection } from './ValuationFormSection';


export const MaterialInputDialog: React.SFC<IMaterialInputDialogProps> = ({
    focus,
    context,
    entity,
    material,
    onSubmitMaterial,
    onCopy,
    onImportComplete,
    disabled,
    ...props
}: IMaterialInputDialogProps) => {

    useEffect(() => {
        if (material)
            setModel(material);
    }, [material, focus]);


    const onUpdateSelection = async (property: string, opt: IMaterialItem | IMaterialItem[] | IMaterialNote[] | number | string | string[] | null) => {

        return new Promise((resolve, reject) => {
            if (opt !== null) {

                let m: any = cloneDeep(model);

                m[property] = opt;

                if (property === "valueEstimate" || property === "quantity") {

                    let qty = m.quantity ?? 1;

                    m.totalValueEstimate = Math.round((m.valueEstimate as number * qty) * 100) / 100;
                }

                if (property === "l1") {
                    m.l2 = undefined
                }

                setModel(m);

            }

            setErrorMessage(null);
            resolve(undefined);
        })



    }


    const onSplit = (qty: number) => {
        if (model.quantity) {


            let newQty = model.quantity - qty;
            let total = model.totalValueEstimate ?? 0;
            let updatedTotalValue = Math.round(((total / model.quantity) * newQty) * 100) / 100;
            let newTotalValue = total - updatedTotalValue;

            let current = cloneDeep(model);
            current.quantity = newQty;
            current.totalValueEstimate = updatedTotalValue;

            let split = cloneDeep(model);
            split.materialId = undefined;
            split.quantity = qty;
            split.totalValueEstimate = newTotalValue;

            //clear out the location info
            split.warehouse = undefined;
            split.rack = undefined;
            split.row = undefined;
            split.pallet = undefined;

            onSubmitMaterial(current, split);

            setOpen(false);
        }
    }

    const onImported = (materials: IMaterial[]) => {
        if (onImportComplete)
            onImportComplete(materials);
        else window.location.reload();

        setOpen(false);
    }

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {

        e.preventDefault();

        if (e.currentTarget.checkValidity()) {
            let m: IMaterial = cloneDeep(model);

            //need to get the correct id for the default selections
            //if they have not been changed

            if (m.unitOfQuantity.id === "")
                await Values.setValues("uoms", (values: IComboBoxOption[]) => {
                    m.unitOfQuantity = values.filter(x =>
                        x.value === m.unitOfQuantity.value
                    )[0] as IMaterialItem;
                });

            if (!m.weightEstimate)
                m.weightEstimate = "0";

            m.classes = m.vehicleClasses?.map(x => x.value).join(", ");

            onSubmitMaterial(m);

            if (!isUpdateContext())
                setModel(defaultMaterialData);

            setOpen(false);
        } else {

        }

    }

    const onDuplicate = async (e: React.FormEvent<HTMLFormElement>) => {

        e.preventDefault();
        if (onCopy) onCopy();
        setCopy(false);
    }

    const onCancel = () => {

        if (!isUpdateContext())
            setModel(defaultMaterialData);

        setOpen(false);
        setCopy(false);
    }

    const defaultMaterialData: IMaterial = {
        l3: "",
        quantity: 1,
        unitOfQuantity: { value: "Each", id: "" } as IMaterialItem,
        weightEstimate: "0",
        ownedBy: "",
        partNumber: "",
        catalogueNumber: "",
        manufacturerNumber: "",
        serialNumber: "",
        vehicleNumber: "",
        unitNumber: "",
        porterbrookAssetNumber: "",
        vehicleClasses: [],
        valueEstimate: 0,
        totalValueEstimate: 0,
        images: [],
        statusId: 1
    };

    const isUpdateContext = () => {

        //are we making changes to an existing
        //material or are we adding a new material

        return [
            MaterialContext.MaterialRow,
            MaterialContext.MaterialRowApproved,
            MaterialContext.Inventory
        ].includes(context);
    }

    const canSetLocation = () => {

        //can the location details be seen 
        //and altered for an existing material

        return [
            MaterialContext.MaterialRowApproved,
            MaterialContext.Inventory
        ].includes(context);
    }

    const canSplitMaterial = () => {

        //can the material be divided and have
        //multiple locations

        return [
            MaterialContext.MaterialRowApproved
        ].includes(context);;
    }

    const canUpdateQuantity = () => {
        return !isUpdateContext() || !model.statusId || model.statusId === 1;
    }

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(0);
    const [model, setModel] = useState<IMaterial>(material ?? defaultMaterialData);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [copy, setCopy] = useState(false);




    return (
        <span>



            {isUpdateContext() ?
                <>
                    <Button size="small" variant={focus ? "contained" : "outlined"} onClick={() => setOpen(true)} disabled={disabled}>
                        Update
                    </Button>
                    {onCopy &&
                        <Button size="small" variant={"outlined"} color="warning" onClick={() => setCopy(true)} disabled={disabled}>
                            Copy
                        </Button>
                    }
                </>
                :
                <Button variant={focus ? "contained" : "outlined"} onClick={() => setOpen(true)} disabled={disabled}>
                    Add Material
                </Button>
            }

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <form
                    onSubmit={(e) => onSubmit(e)}
                    onInvalid={() => setErrorMessage("please check that all required fields have been populated")}
                >
                    <DialogTitle>
                        {isUpdateContext() ? "Update Material"
                            : "Add Material"}
                    </DialogTitle>
                    <DialogContent>

                        <Grid container>

                            {!isUpdateContext() && //import is only available when adding material
                                <Grid item xs={12}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <Tabs value={value} onChange={(_, value) => setValue(value)} aria-label="sr-table-tabs">
                                            <Tab
                                                label="Manual Entry"
                                                id={`sr-table-tab-${0}`}
                                            />
                                            <Tab
                                                label="Import"
                                                id={`sr-table-tab-${1}`}
                                            />
                                        </Tabs>
                                    </Box>
                                </Grid>
                            }


                            <Grid item xs={12}>
                                <TabPanel value={value} index={0}>

                                    <MaterialDescriptionFormSection
                                        material={model}
                                        onUpdate={onUpdateSelection}
                                        isUpdate={isUpdateContext()}
                                        canUpdateQuantity={canUpdateQuantity()}
                                        context={context}
                                        isContract={["CONTRACT", "CONTRACTVARIATION"].includes(entity.type)}
                                    />

                                    <PartIdentificationFormSection
                                        material={model}
                                        onUpdate={onUpdateSelection}
                                        isContract={["CONTRACT", "CONTRACTVARIATION"].includes(entity.type)}
                                    />

                                    <RestrictionsFormSection
                                        material={model}
                                        onUpdate={onUpdateSelection}
                                    />

                                    <ValuationFormSection
                                        material={model}
                                        onUpdate={onUpdateSelection}
                                    />

                                    {canSetLocation() &&
                                        <LocationFormSection
                                            material={model}
                                            onUpdate={onUpdateSelection}
                                            onSplit={canSplitMaterial() ? onSplit : undefined}
                                        />
                                    }

                                    <NotesFormSection
                                        material={model}
                                        onUpdate={onUpdateSelection}
                                    />


                                    {errorMessage &&
                                        <DialogContentText color="error">
                                            {errorMessage}
                                        </DialogContentText>
                                    }

                                </TabPanel>
                                <TabPanel value={value} index={1}>
                                    <ImportFormSection
                                        entity={entity}
                                        material={model}
                                        onUpdate={() => {
                                            window.location.reload();
                                            return Promise.resolve();
                                        }}
                                        onImported={onImported}
                                    />
                                </TabPanel>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancel} color="warning">Cancel</Button>

                        {value === 0 &&
                            <Button type="submit">{isUpdateContext() ? "Update" : "Add"}</Button>
                        }
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog
                open={copy}
                onClose={() => setCopy(false)}

                maxWidth="md"
            >
                <form
                    onSubmit={(e) => onDuplicate(e)}
                    onInvalid={() => setErrorMessage("please check that all required fields have been populated")}
                >
                    <DialogTitle>
                        Copy material
                    </DialogTitle>
                    <DialogContent>

                        Are you sure you want to copy this material?

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancel} color="warning">Cancel</Button>
                        <Button type="submit">Copy</Button>
                    </DialogActions>
                </form>
            </Dialog>


        </span>
    );
}
